import React from "react";
import PropTypes from "prop-types";

// Backend rates are fractions in [0, 1].
const pct = value => (value == null ? 0 : value * 100);
const formatRate = value => `${pct(value).toFixed(1)}%`;
const formatInt = value => (value == null ? 0 : value).toLocaleString();

// Card accent colors (mockup theme).
const COLORS = {
  sent: "#0ea5e9", // sky
  delivered: "#10b981", // emerald
  opened: "#1a3a5c", // navy
  ignored: "#f59e0b", // amber
  bounced: "#ef4444" // red
};

const MetricCard = ({ label, value, rate, rateLabel, color, wide }) => (
  <div className={`metric-card${wide ? " wide" : ""}`}>
    <span className="metric-accent" style={{ backgroundColor: color }} />
    <div className="metric-body">
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      {rate != null && (
        <React.Fragment>
          <div className="metric-rate">
            {formatRate(rate)}
            {rateLabel ? <span className="metric-rate-label"> {rateLabel}</span> : null}
          </div>
          <div className="metric-progress">
            <div
              className="metric-progress-fill"
              style={{
                width: `${Math.min(100, Math.max(0, pct(rate)))}%`,
                backgroundColor: color
              }}
            />
          </div>
        </React.Fragment>
      )}
    </div>
  </div>
);

MetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  rate: PropTypes.number,
  rateLabel: PropTypes.string,
  color: PropTypes.string.isRequired,
  wide: PropTypes.bool
};

const EmailQualityKpis = ({ summary }) => {
  const s = summary || {};
  const delivered = s.Delivered || 0;
  const bounced = (s.HardBounced || 0) + (s.SoftBounced || 0);
  // Ignored share of delivered mail (guard divide-by-zero).
  const ignoredRate = delivered > 0 ? (s.Ignored || 0) / delivered : 0;

  return (
    <div className="email-quality-kpis">
      <MetricCard
        label="Total Sent"
        value={formatInt(s.Sent)}
        color={COLORS.sent}
        wide
      />
      <MetricCard
        label="Delivered"
        value={formatInt(s.Delivered)}
        rate={s.DeliveryRate}
        rateLabel="of sent"
        color={COLORS.delivered}
      />
      <MetricCard
        label="Opened"
        value={formatInt(s.Opened)}
        rate={s.OpenRate}
        rateLabel="of delivered"
        color={COLORS.opened}
      />
      <MetricCard
        label="Ignored"
        value={formatInt(s.Ignored)}
        rate={ignoredRate}
        rateLabel="of delivered"
        color={COLORS.ignored}
      />
      <MetricCard
        label="Bounced"
        value={formatInt(bounced)}
        rate={s.BounceRate}
        rateLabel="hard, of sent"
        color={COLORS.bounced}
      />
    </div>
  );
};

EmailQualityKpis.propTypes = {
  summary: PropTypes.shape({
    Sent: PropTypes.number,
    Delivered: PropTypes.number,
    Opened: PropTypes.number,
    Ignored: PropTypes.number,
    HardBounced: PropTypes.number,
    SoftBounced: PropTypes.number,
    DeliveryRate: PropTypes.number,
    OpenRate: PropTypes.number,
    BounceRate: PropTypes.number
  })
};

export default EmailQualityKpis;
